// 翻訳（日本語→英語）— 稼働状況と英語表記の固定（用語集）
// 英語はローカルLLMが生成する派生表示。日本語が唯一の正本。
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Languages, Gauge, Lock, Trash2, Zap, Plus, Search, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { formatDateTime } from '@/utils/datetime';

const TR = `${process.env.REACT_APP_BACKEND_URL}/api/translate`;
const EV = `${process.env.REACT_APP_BACKEND_URL}/api/evidence`;

const Metric = ({ label, value, tone = 'zinc', testid }) => (
  <div className={`rounded-lg border px-3 py-2 ${{
    rose: 'border-rose-200 bg-rose-50',
    amber: 'border-amber-200 bg-amber-50',
    zinc: 'border-zinc-200 bg-white',
  }[tone] || 'border-zinc-200 bg-white'}`} data-testid={testid}>
    <p className="text-[10.5px] text-zinc-500">{label}</p>
    <p className={`text-[15px] font-bold tabular-nums ${{
      rose: 'text-rose-700', amber: 'text-amber-800', zinc: 'text-zinc-900',
    }[tone] || 'text-zinc-900'}`}>
      {value}
    </p>
  </div>
);

export function TranslationHealthSection({ readOnly = false }) {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setH((await axios.get(`${TR}/health?limit=25`)).data); }
    catch { setH(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const purge = async () => {
    if (!window.confirm('保存済みの英語翻訳をすべて削除します。日本語の原本は変わりません。実行しますか？')) return;
    setBusy(true);
    try {
      const r = await axios.post(`${TR}/cache/purge`, {});
      toast.success(`翻訳キャッシュを削除しました（${r.data.purged}件）`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
    finally { setBusy(false); }
  };

  const warm = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${TR}/prewarm/run`, {});
      if (r.data?.skipped === 'ai_busy') toast.info('他のAI処理が動いているため見送りました');
      else if (r.data?.skipped) toast.info(`見送りました（${r.data.skipped}）`);
      else if (r.data?.stopped === 'consecutive_failures') {
        toast.warning(`失敗が3回続いたため停止しました（${r.data.warmed || 0}件完了）`);
      } else {
        toast.success(r.data?.warmed ? `先読み翻訳を実行しました（${r.data.warmed}件）`
          : '先読みが必要な文字列はありません');
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '実行に失敗しました'); }
    finally { setBusy(false); }
  };

  const toggleManual = async (v) => {
    setBusy(true);
    try {
      await axios.post(`${TR}/prewarm/settings`, { manual_allowed: v });
      toast.success(v ? '手動の先読みを有効にしました' : '手動の先読みを停止しました');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '変更に失敗しました'); }
    finally { setBusy(false); }
  };

  // T1 — 壊れた英訳（表・用語集の流出・装飾・数値落ち）だけを選んで削除する
  const sweepBad = async () => {
    setBusy(true);
    try {
      const dry = await axios.post(`${TR}/cache/sweep-bad?dry_run=true`, {});
      const found = dry.data?.found || 0;
      if (!found) { toast.success('壊れた英訳は見つかりませんでした'); return; }
      const sample = (dry.data?.rows || []).slice(0, 5)
        .map((r) => `・${r.source_text} → ${r.translated_text}（${r.reason}）`).join('\n');
      if (!window.confirm(`壊れた英訳が ${found} 件見つかりました。削除しますか？\n（用語集と正常な訳は触りません）\n\n${sample}`)) return;
      const run = await axios.post(`${TR}/cache/sweep-bad?dry_run=false`, {});
      toast.success(`${run.data?.invalidated || 0} 件を削除、${run.data?.cleaned_in_place || 0} 件を修正しました`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '実行に失敗しました'); }
    finally { setBusy(false); }
  };

  if (!h) return <p className="text-xs text-zinc-400" data-testid="translation-health-loading">読み込み中…</p>;
  const s = h.summary || {};
  const pw = h.prewarm || {};

  return (
    <div className="space-y-3" data-testid="translation-health">
      <div className="rounded-lg border border-zinc-200 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Languages size={16} className="text-indigo-600" />
          <h3 className="text-[13px] font-bold text-zinc-800">翻訳の稼働状況</h3>
          <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${h.enabled
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-zinc-200 bg-zinc-50 text-zinc-500'}`} data-testid="translation-health-enabled">
            {h.enabled ? '有効' : '停止中'}
          </span>
          <span className="ml-auto text-[11px] text-zinc-500">
            モデル <b className="font-mono text-zinc-700">{h.model || '未設定'}</b>
            <span className="mx-1.5 text-zinc-300">|</span>
            プロンプト {h.prompt_version}
          </span>
        </div>
        <p className="mt-1.5 text-[11px] text-zinc-500">
          日本語が正本です。英語は表示のたびにローカルLLM（機能ルーティングの「翻訳」）が生成し、
          結果はキャッシュされます。日本語を編集すると古い英語は自動的に無効になります。
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Metric label="キャッシュ再利用率" value={`${s.cache_hit_rate ?? 0}%`} testid="tr-metric-hitrate" />
          <Metric label="キャッシュ済み件数" value={h.cached_entries ?? 0} testid="tr-metric-cached" />
          <Metric label="平均応答" value={`${s.avg_latency_ms ?? 0} ms`} testid="tr-metric-latency" />
          <Metric label="失敗（直近）" value={s.failures ?? 0}
            tone={(s.failures || 0) > 0 ? 'rose' : 'zinc'} testid="tr-metric-failures" />
          <Metric label="用語集で確定" value={s.glossary_hits ?? 0} testid="tr-metric-glossary" />
          <Metric label="先読み要求" value={s.prewarm_requests ?? 0} testid="tr-metric-prewarm" />
          <Metric label="キャッシュ未命中" value={s.cache_misses ?? 0} testid="tr-metric-misses" />
          <Metric label="リクエスト（直近）" value={s.requests ?? 0} testid="tr-metric-requests" />
        </div>
        {Object.keys(s.errors_by_category || {}).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="tr-error-categories">
            {Object.entries(s.errors_by_category).map(([k, v]) => (
              <span key={k} className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10.5px] text-rose-700">
                {k}: {v}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* iter_314 · 転送契約と思考制御の状態（2026-06 修復） */}
      <div className="rounded-lg border border-zinc-200 p-4" data-testid="tr-repair-status">
        <div className="flex flex-wrap items-center gap-2">
          <ShieldCheck size={15} className="text-emerald-600" />
          <h3 className="text-[13px] font-bold text-zinc-800">転送契約と思考制御（2026-06 修復）</h3>
        </div>
        <p className="mt-1.5 text-[11px] text-zinc-500">
          機能側の設定が通信方式（ストリーム・モデル・メッセージ）を上書きできないよう固定しています。
          モデルの「思考」出力は受信時に必ず除去し、トークン切れは別分類で記録します。
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Metric label="転送設定の上書き拒否"
            value={s.transport_guard?.rejected_total ?? 0}
            tone={(s.transport_guard?.rejected_total || 0) > 0 ? 'amber' : 'zinc'}
            testid="tr-metric-guard" />
          <Metric label="項目タイムアウト"
            value={`${h.output_budget?.item_timeout_s ?? '—'} 秒`} testid="tr-metric-item-timeout" />
          <Metric label="リクエスト締切"
            value={`${h.output_budget?.request_deadline_s ?? '—'} 秒`} testid="tr-metric-deadline" />
          <Metric label="出力トークン下限"
            value={h.output_budget?.min_tokens ?? '—'} testid="tr-metric-mintokens" />
        </div>
        {Object.keys(s.output_states || {}).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="tr-output-states">
            {Object.entries(s.output_states).map(([k, v]) => (
              <span key={k} className={`rounded-full border px-2 py-0.5 text-[10.5px] ${k === 'content'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                {{
                  content: '正常', empty: '空応答', reasoning_only: '推論のみ',
                  thinking_only: '思考のみ', thinking_unclosed: '思考が未完了',
                  truncated: 'トークン切れ', malformed_stream: '形式不正',
                  provider_protocol_mismatch: '通信方式の不一致',
                  provider_error: 'プロバイダ異常', cancelled: '中断',
                }[k] || k}: {v}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-2 text-[11px] text-zinc-600"
          data-testid="tr-thinking-control">
          <p>
            思考停止オプション（このモデル）:{' '}
            <b className="text-zinc-800">{{
              supported: '実測で対応を確認', rejected: 'サーバーが拒否',
              accepted_no_effect: '受け付けるが効果なし',
              inconclusive_model_does_not_think: '判定不能（このモデルは思考しない）',
              provider_unreachable: 'モデルに接続できず未実測',
              unprobed: '未実測',
            }[h.thinking_control?.result] || h.thinking_control?.result || '未実測'}</b>
            {h.thinking_control?.probed_at
              ? `（${formatDateTime(h.thinking_control.probed_at)}）` : ''}
          </p>
          <p className="mt-0.5">
            送信の有効化: {h.thinking_control?.admin_enabled ? '有効' : '無効（既定）'} ／
            受信側の思考除去: 常に有効（モデルに依存しません）
          </p>
          <p className="mt-0.5 text-zinc-500">
            モデル名からの推測では送信しません。実測で対応が確認された場合のみ、管理者が有効化できます。
          </p>
        </div>
        {h.repair_flags && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="tr-repair-flags">
            {Object.entries(h.repair_flags).map(([k, v]) => (
              <span key={k} className={`rounded-full border px-2 py-0.5 text-[10.5px] ${v
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-zinc-200 bg-zinc-50 text-zinc-500'}`}>
                {{
                  transport_reserved_fields_enabled: '転送設定の保護',
                  structured_completion_normalisation_enabled: '完了結果の正規化',
                  strict_output_validation_enabled: '厳格な出力検証',
                  adaptive_batching_enabled: 'まとめ翻訳',
                  automatic_browser_prewarm_enabled: 'ブラウザ先読み',
                  automatic_server_prewarm_enabled: 'サーバー定期先読み',
                  manual_admin_prewarm_enabled: '手動先読み（診断）',
                  provider_specific_thinking_control_enabled: '思考停止の送信',
                }[k] || k}: {v ? 'ON' : 'OFF'}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-zinc-200 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Zap size={15} className="text-amber-500" />
          <h3 className="text-[13px] font-bold text-zinc-800">先読み翻訳（プリウォーム）</h3>
          <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${pw.enabled && !pw.server_suspended
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
            {pw.server_suspended || pw.client_suspended ? '自動実行 停止中（封じ込め）'
              : pw.enabled ? '有効' : '停止中'}
          </span>
        </div>
        {(pw.server_suspended || pw.client_suspended) && (
          <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900"
            data-testid="tr-prewarm-containment">
            <p className="font-semibold">自動の先読みは意図的に停止しています。</p>
            <p className="mt-0.5">
              理由: {pw.suspended_reason === 'translation_transport_incident'
                ? '翻訳の転送層の不具合により先読みが全件失敗し、共有ローカルモデルを占有していたため'
                : pw.suspended_reason || '—'}
              {pw.suspended_at ? `（${formatDateTime(pw.suspended_at)}）` : ''}
            </p>
            <p className="mt-0.5">
              ブラウザ側 {pw.client_suspended ? '停止' : '稼働'} ／
              サーバー定期 {pw.server_suspended ? '停止' : '稼働'} ／
              管理者の手動実行 {pw.manual_allowed ? '可' : '不可'}（診断用・最大24件）。
              再開は修復完了後に管理者の承認で行います。
            </p>
          </div>
        )}
        <p className="mt-1.5 text-[11px] text-zinc-500">
          対象は<b>用語集の見出し語と固定UIラベルだけ</b>です（人名・会社名・品名などの業務データは
          一切含みません）。1件ずつ順番に実行し、失敗が{pw.max_consecutive_failures || 3}回続いたら止まります。
          他のAI処理が動いている間は実行しません。
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Metric label="記録済み文字列" value={pw.tracked_strings ?? 0} testid="tr-metric-tracked" />
          <Metric label="前回の先読み件数" value={pw.last_warmed ?? 0} testid="tr-metric-lastwarmed" />
          <Metric label="前回実行"
            value={pw.last_run ? formatDateTime(pw.last_run) : '未実行'} testid="tr-metric-lastrun" />
        </div>
        {pw.last_stopped === 'consecutive_failures' && (
          <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900"
            data-testid="tr-prewarm-stopped">
            前回は失敗が続いたため、途中で安全に停止しました。
          </p>
        )}
        {!readOnly && (
          <>
            <label className="mt-3 flex items-center gap-2 text-[11px] text-zinc-600">
              <Switch checked={pw.manual_allowed !== false} disabled={busy}
                onCheckedChange={toggleManual} data-testid="tr-prewarm-toggle" />
              手動の先読みを許可する（管理者がボタンを押したときだけ動きます）
            </label>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="h-8 text-[11px]"
                disabled={busy || pw.manual_allowed === false}
                onClick={warm} data-testid="tr-prewarm-run">
                <Zap size={12} className="mr-1" /> 今すぐ先読みする
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                className="h-8 text-[11px] text-amber-700 hover:bg-amber-50"
                onClick={sweepBad} data-testid="tr-cache-sweep-bad">
                <ShieldCheck size={12} className="mr-1" /> 壊れた英訳だけを削除
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                className="h-8 text-[11px] text-rose-600 hover:bg-rose-50"
                onClick={purge} data-testid="tr-cache-purge">
                <Trash2 size={12} className="mr-1" /> 翻訳キャッシュを削除
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border border-zinc-200 p-4">
        <div className="flex items-center gap-2">
          <Gauge size={15} className="text-zinc-500" />
          <h3 className="text-[13px] font-bold text-zinc-800">直近のリクエスト</h3>
          <span className="ml-auto text-[10.5px] text-zinc-400">本文は記録していません（ハッシュと件数のみ）</span>
        </div>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="text-zinc-500">
              <tr className="border-b border-zinc-200">
                <th className="py-1.5 text-left">時刻</th>
                <th className="py-1.5 text-right">件数</th>
                <th className="py-1.5 text-right">文字数</th>
                <th className="py-1.5 text-right">再利用</th>
                <th className="py-1.5 text-right">応答</th>
                <th className="py-1.5 text-left">状態</th>
              </tr>
            </thead>
            <tbody data-testid="tr-recent-rows">
              {(h.recent || []).length === 0 && (
                <tr><td colSpan={6} className="py-3 text-center text-zinc-400">記録がありません</td></tr>
              )}
              {(h.recent || []).map((r) => (
                <tr key={r.id} className="border-b border-zinc-100">
                  <td className="py-1.5 text-zinc-600">{formatDateTime(r.at)}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.items}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.chars}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.cache_hits}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.latency_ms} ms</td>
                  <td className="py-1.5">
                    {r.error_category
                      ? <span className="text-rose-700">{r.error_category}</span>
                      : <span className="text-emerald-700">
                          {r.prewarm ? '先読み完了' : '完了'}
                        </span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── 英語表記の固定（社内用語辞書と共通） ─────────────────────────────────
export function TranslationGlossarySection() {
  const [terms, setTerms] = useState([]);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState({ term_ja: '', term_en: '', note: '', exact_only: false });
  const [bulk, setBulk] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${EV}/terminology`);
      setTerms(r.data.terms || []);
    } catch { setTerms([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const notifyPurge = (res) => {
    const n = res?.data?.translation_cache?.purged_translations;
    if (n) toast.info(`用語集の変更により、保存済みの英語翻訳 ${n} 件を作り直します`);
  };

  const add = async () => {
    if (!draft.term_ja.trim() || !draft.term_en.trim()) {
      toast.error('日本語の用語と英語の訳を入力してください');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${EV}/terminology`, {
        term_ja: draft.term_ja.trim(), term_en: draft.term_en.trim(),
        note: draft.note.trim(), exact_only: draft.exact_only,
        translation_lock: true, review_status: 'approved',
      });
      toast.success('用語を登録しました');
      notifyPurge(res);
      setDraft({ term_ja: '', term_en: '', note: '', exact_only: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '登録に失敗しました'); }
    finally { setBusy(false); }
  };

  const patch = async (t, partial) => {
    try {
      const res = await axios.patch(`${EV}/terminology/${t.id}`, {
        term_ja: t.term_ja, term_en: t.term_en, aliases: t.aliases || [],
        note: t.note || '', relation: t.relation || 'exact',
        review_status: t.review_status || 'approved',
        translation_lock: t.translation_lock !== false, exact_only: !!t.exact_only,
        ...partial,
      });
      notifyPurge(res);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '更新に失敗しました'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`「${t.term_ja}」を用語辞書から削除しますか？`)) return;
    try {
      const res = await axios.delete(`${EV}/terminology/${t.id}`);
      toast.success('削除しました');
      notifyPurge(res);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  const addBulk = async () => {
    const lines = bulk.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    setBusy(true);
    let ok = 0;
    let exactCount = 0;
    for (const line of lines.slice(0, 100)) {
      // T5: 「日本語, 英語」または「日本語, 英語, exact」（3列目=完全一致のみ）
      const parts = line.split(/[\t,、=]+/).map((x) => (x || '').trim());
      const [ja, en, flag] = parts;
      if (!ja || !en) continue;
      const exactOnly = /^(exact|完全一致|完全|exact_only|true|1)$/i.test(flag || '');
      try {
        await axios.post(`${EV}/terminology`, {
          term_ja: ja, term_en: en, exact_only: exactOnly,
          translation_lock: true, review_status: 'approved',
        });
        ok += 1;
        if (exactOnly) exactCount += 1;
      } catch { /* skip the bad line */ }
    }
    setBusy(false);
    setBulk('');
    toast.success(`${ok} 件を登録しました${exactCount ? `（うち完全一致のみ ${exactCount} 件）` : ''}`);
    load();
  };

  // T5 — 英語の訳をその場で直す（行の入力欄 → Enter / フォーカスを外すと保存）
  const [enDraft, setEnDraft] = useState({});
  const saveEn = async (t) => {
    const next = (enDraft[t.id] ?? '').trim();
    setEnDraft((p) => { const n = { ...p }; delete n[t.id]; return n; });
    if (!next || next === (t.term_en || '')) return;
    await patch(t, { term_en: next });
    toast.success('英語の訳を更新しました');
  };

  const filtered = terms.filter((t) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (t.term_ja || '').toLowerCase().includes(s)
      || (t.term_en || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-3" data-testid="translation-glossary">
      <div className="rounded-lg border border-zinc-200 p-4">
        <div className="flex items-center gap-2">
          <Lock size={15} className="text-indigo-600" />
          <h3 className="text-[13px] font-bold text-zinc-800">英語表記の固定（用語集）</h3>
          <span className="ml-auto text-[10.5px] text-zinc-400">社内用語辞書と共通 · {terms.length} 件</span>
        </div>
        <p className="mt-1.5 text-[11px] text-zinc-500">
          ここで英訳を決めた用語は、AIの判断より優先されます。用語だけの表示はLLMを呼ばずに即座に確定し、
          文章中に含まれる場合は「必ずこの訳を使う」指示としてモデルに渡されます。
          変更すると保存済みの英語翻訳は作り直されます。
        </p>

        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <Input value={draft.term_ja} maxLength={60} placeholder="日本語の用語（例: 製作台数）"
            onChange={(e) => setDraft({ ...draft, term_ja: e.target.value })}
            className="h-9 text-[12px]" data-no-translate data-testid="glossary-new-ja" />
          <Input value={draft.term_en} maxLength={80} placeholder="英語の訳（例: Units to build）"
            onChange={(e) => setDraft({ ...draft, term_en: e.target.value })}
            className="h-9 text-[12px]" data-testid="glossary-new-en" />
          <Input value={draft.note} maxLength={120} placeholder="備考（任意）"
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            className="h-9 text-[12px]" data-testid="glossary-new-note" />
          <Button size="sm" className="h-9 text-[11px]" disabled={busy} onClick={add}
            data-testid="glossary-add">
            <Plus size={12} className="mr-1" /> 登録
          </Button>
        </div>
        <label className="mt-2 flex items-center gap-2 text-[11px] text-zinc-600">
          <Switch checked={draft.exact_only} data-testid="glossary-new-exact"
            onCheckedChange={(v) => setDraft({ ...draft, exact_only: v })} />
          完全一致のみに適用する（文章の中では使わない）
        </label>
      </div>

      <div className="rounded-lg border border-zinc-200 p-4">
        <div className="relative mb-2">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="用語を検索"
            className="h-8 pl-8 text-[12px]" data-testid="glossary-search" />
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-[11.5px]">
            <thead className="sticky top-0 bg-white text-zinc-500">
              <tr className="border-b border-zinc-200">
                <th className="py-1.5 text-left">日本語の用語</th>
                <th className="py-1.5 text-left">英語の訳</th>
                <th className="py-1.5 text-center">固定</th>
                <th className="py-1.5 text-center">完全一致のみ</th>
                <th className="py-1.5" />
              </tr>
            </thead>
            <tbody data-testid="glossary-rows">
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-center text-zinc-400">用語がありません</td></tr>
              )}
              {filtered.map((t) => (
                <tr key={t.id} className="border-b border-zinc-100" data-testid={`glossary-row-${t.id}`}>
                  <td className="py-1.5 pr-2 text-zinc-800" data-no-translate
                    data-testid={`glossary-ja-${t.id}`}>{t.term_ja}</td>
                  <td className="py-1.5 pr-2 text-zinc-700">
                    <Input
                      value={enDraft[t.id] ?? (t.term_en || '')}
                      maxLength={80}
                      placeholder="未設定"
                      onChange={(e) => setEnDraft((p) => ({ ...p, [t.id]: e.target.value }))}
                      onBlur={() => (enDraft[t.id] !== undefined ? saveEn(t) : null)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      className="h-7 text-[11.5px]"
                      data-testid={`glossary-en-input-${t.id}`}
                    />
                  </td>
                  <td className="py-1.5 text-center">
                    <Switch checked={t.translation_lock !== false}
                      data-testid={`glossary-lock-${t.id}`}
                      onCheckedChange={(v) => patch(t, { translation_lock: v })} />
                  </td>
                  <td className="py-1.5 text-center">
                    <Switch checked={!!t.exact_only}
                      data-testid={`glossary-exact-${t.id}`}
                      onCheckedChange={(v) => patch(t, { exact_only: v })} />
                  </td>
                  <td className="py-1.5 text-right">
                    <Button size="sm" variant="ghost"
                      className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
                      onClick={() => remove(t)} data-testid={`glossary-remove-${t.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-zinc-300 p-4">
        <p className="text-[11px] font-semibold text-zinc-600">まとめて追加</p>
        <p className="mt-0.5 text-[10.5px] text-zinc-400">
          1行に「日本語, 英語」または「日本語, 英語, exact」の形式で貼り付けてください
          （3列目に <code>exact</code> を書くと「完全一致のみ」で登録します。
          タブ・カンマ・= 区切りに対応、最大100行）。
        </p>
        <textarea value={bulk} onChange={(e) => setBulk(e.target.value)} rows={4}
          className="mt-2 w-full rounded-md border border-zinc-200 p-2 text-[12px]"
          placeholder={'製作台数, Units to build\n員数, Pieces per unit\nセンスビー製造部, Sensbey Production, exact'}
          data-no-translate data-testid="glossary-bulk-input" />
        <Button size="sm" variant="outline" className="mt-2 h-8 text-[11px]"
          disabled={busy || !bulk.trim()} onClick={addBulk} data-testid="glossary-bulk-add">
          まとめて登録
        </Button>
      </div>
    </div>
  );
}

export default function TranslateSection() {
  return (
    <div className="space-y-4" data-testid="ai-console-translate">
      <TranslationHealthSection />
      <TranslationGlossarySection />
    </div>
  );
}
